import { CornerUpLeft, Download, FileText, Pencil, Pin, SmilePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import {
  useDeleteMessage,
  useEditMessage,
  useTogglePin,
  useToggleReaction,
} from '../../hooks/useTeamChat';
import type { ChatReadReceipt, TeamChatAttachment, TeamChatMessage } from '../../lib/teamChatApi';
import { Markdown } from '../chat/Markdown';
import { Avatar } from '../tasks/linear/Avatar';
import { Popover } from '../tasks/linear/Popover';
import { AttachmentImage } from '../ui/AttachmentImage';
import { useConfirm } from '../ui/ConfirmProvider';
import { EmbedCard } from './EmbedCard';
import { EmojiPicker } from './EmojiPicker';
import { ReactionBar } from './ReactionBar';
import { SeenTicks } from './SeenTicks';

function formatTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function formatBytes(size: number): string {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

function FileChip({ attachment }: { attachment: TeamChatAttachment }) {
  return (
    <a
      href={attachment.downloadUrl ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="flex max-w-xs items-center gap-2 rounded-md border border-border bg-accent/50 px-3 py-2 transition-colors hover:bg-accent"
    >
      <FileText size={18} className="shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {attachment.fileName}
        </span>
        <span className="block text-xs text-muted-foreground">{formatBytes(attachment.size)}</span>
      </span>
      <Download size={14} className="shrink-0 text-muted-foreground" />
    </a>
  );
}

function AttachmentChip({ attachment }: { attachment: TeamChatAttachment }) {
  if (attachment.contentType.startsWith('image/') && attachment.downloadUrl) {
    return (
      <AttachmentImage
        src={attachment.downloadUrl}
        alt={attachment.fileName}
        className="max-h-64 max-w-xs rounded-md border border-border object-contain"
        placeholderClassName="h-40 w-56"
        fallback={<FileChip attachment={attachment} />}
      />
    );
  }
  return <FileChip attachment={attachment} />;
}

/**
 * One message row, Discord-style: consecutive messages from the same author
 * collapse the avatar/header (`showHeader`). Hover reveals the action bar
 * (react / reply / pin / edit / delete).
 */
export function MessageItem({
  message,
  meId,
  isAdmin,
  showHeader,
  receipts,
  isGroup,
  onReply,
  onJumpTo,
}: {
  message: TeamChatMessage;
  meId: string | undefined;
  isAdmin: boolean;
  showHeader: boolean;
  receipts?: ChatReadReceipt[];
  isGroup?: boolean;
  onReply: (message: TeamChatMessage) => void;
  onJumpTo?: (messageId: string) => void;
}) {
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const editMessage = useEditMessage();
  const deleteMessage = useDeleteMessage();
  const togglePin = useTogglePin();
  const toggleReaction = useToggleReaction();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Touch has no hover, so on a phone the action bar (react / reply / pin /
  // edit / delete) was simply unreachable — pinning looked broken because it
  // could not be started. Tapping the message reveals it.
  const [tapped, setTapped] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // A tap anywhere outside the message closes its bar again (another
  // message, the composer, empty space). Popovers it opened (the emoji
  // picker) live in a portal, outside the row: those don't count.
  useEffect(() => {
    if (!tapped) return;
    function onDown(e: PointerEvent) {
      const target = e.target as Node;
      if (rowRef.current?.contains(target)) return;
      if ((target as HTMLElement).closest?.('.ui-popover-panel')) return;
      setTapped(false);
    }
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [tapped]);

  const isMine = message.author.id === meId;
  const canEdit = isMine && !message.deleted;
  const canDelete = (isMine || isAdmin) && !message.deleted;

  const startEdit = () => {
    setDraft(message.body);
    setEditing(true);
  };

  const saveEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === message.body) {
      setEditing(false);
      return;
    }
    editMessage.mutate({ id: message.id, body: trimmed }, { onSettled: () => setEditing(false) });
  };

  const remove = async () => {
    if (!(await confirm({ message: t('teamChat.confirmDeleteMessage'), danger: true }))) return;
    deleteMessage.mutate(message.id);
  };

  const react = (emoji: string) => toggleReaction.mutate({ id: message.id, emoji });

  // Don't hijack taps on links, buttons or a text selection.
  const onRowClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a, button, textarea')) return;
    if (window.getSelection()?.toString()) return;
    setTapped((v) => !v);
  };

  const actions = (
    <>
      <Popover
        width={280}
        align="end"
        trigger={({ toggle, ref }) => (
          <button
            type="button"
            ref={(el) => ref(el)}
            onClick={toggle}
            aria-label={t('teamChat.addReaction')}
            title={t('teamChat.addReaction')}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <SmilePlus size={15} />
          </button>
        )}
      >
        {(close) => (
          <EmojiPicker
            onPick={(emoji) => {
              react(emoji);
              close();
            }}
          />
        )}
      </Popover>
      <button
        type="button"
        onClick={() => {
          onReply(message);
          setTapped(false);
        }}
        aria-label={t('teamChat.reply')}
        title={t('teamChat.reply')}
        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <CornerUpLeft size={15} />
      </button>
      <button
        type="button"
        onClick={() => togglePin.mutate({ id: message.id, pinned: message.pinnedAt !== null })}
        aria-label={message.pinnedAt ? t('teamChat.unpin') : t('teamChat.pin')}
        title={message.pinnedAt ? t('teamChat.unpin') : t('teamChat.pin')}
        className={`rounded p-1 hover:bg-accent hover:text-foreground ${
          message.pinnedAt ? 'text-amber-500' : 'text-muted-foreground'
        }`}
      >
        <Pin size={15} />
      </button>
      {canEdit && (
        <button
          type="button"
          onClick={startEdit}
          aria-label={t('common.edit')}
          title={t('common.edit')}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Pencil size={15} />
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={() => void remove()}
          aria-label={t('common.delete')}
          title={t('common.delete')}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-red-600"
        >
          <Trash2 size={15} />
        </button>
      )}
    </>
  );

  return (
    <div
      id={`team-chat-message-${message.id}`}
      ref={rowRef}
      onClick={onRowClick}
      className={`group relative flex gap-3 px-4 py-0.5 hover:bg-accent/40 ${
        tapped ? 'bg-accent/40' : ''
      } ${showHeader ? 'mt-3' : ''} ${message.pinnedAt ? 'border-l-2 border-amber-400' : ''}`}
    >
      <div className="w-9 shrink-0 pt-0.5">
        {showHeader ? (
          <Avatar name={message.author.name || null} size={36} />
        ) : (
          <span
            className={`w-9 pt-1 text-right text-[10px] text-muted-foreground group-hover:block ${
              tapped ? 'block' : 'hidden'
            }`}
          >
            {formatTime(message.createdAt, locale)}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {showHeader && (
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-foreground">{message.author.name}</span>
            <span className="text-xs text-muted-foreground">
              {formatTime(message.createdAt, locale)}
            </span>
            {message.pinnedAt && <Pin size={12} className="text-amber-500" />}
          </div>
        )}

        {message.replyTo && (
          <button
            type="button"
            onClick={() => onJumpTo?.(message.replyTo!.id)}
            className="mb-0.5 flex max-w-md items-center gap-1.5 truncate text-xs text-muted-foreground hover:text-foreground"
          >
            <CornerUpLeft size={12} className="shrink-0" />
            <span className="font-medium">{message.replyTo.authorName}</span>
            <span className="truncate">
              {message.replyTo.deleted ? t('teamChat.deletedMessage') : message.replyTo.snippet}
            </span>
          </button>
        )}

        {message.deleted ? (
          <p className="text-sm italic text-muted-foreground">{t('teamChat.deletedMessage')}</p>
        ) : editing ? (
          <div className="mt-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveEdit();
                } else if (e.key === 'Escape') {
                  setEditing(false);
                }
              }}
              rows={Math.min(6, Math.max(2, draft.split('\n').length))}
              className="ui-input resize-none"
              autoFocus
            />
            <div className="mt-1 flex gap-2 text-xs">
              <button type="button" onClick={saveEdit} className="text-brand-600 hover:underline">
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-muted-foreground hover:underline"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {message.body && (
              <div className="text-sm text-foreground [overflow-wrap:anywhere]">
                <Markdown content={message.body} />
                {message.editedAt && (
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    ({t('teamChat.edited')})
                  </span>
                )}
              </div>
            )}
            {message.attachments.length > 0 && (
              <div className="mt-1.5 flex flex-col items-start gap-1.5">
                {message.attachments.map((attachment) => (
                  <AttachmentChip key={attachment.key} attachment={attachment} />
                ))}
              </div>
            )}
            {message.embeds.map((embed) => (
              <EmbedCard key={embed.url} embed={embed} />
            ))}
          </>
        )}

        <ReactionBar reactions={message.reactions} onToggle={react} />
        {tapped && !message.deleted && !editing && (
          <div className="mt-1 hidden w-fit items-center gap-0.5 rounded-lg border border-border bg-background p-0.5 shadow-sm touch:flex">
            {actions}
          </div>
        )}
      </div>

      {/* Delivery ticks at the far right of the row, level with the timestamp.
          These are rows, not WhatsApp bubbles — under the text they would just
          float in empty space. */}
      {isMine && !message.deleted && (
        <div className="shrink-0 self-start pt-1">
          <SeenTicks message={message} receipts={receipts} isGroup={isGroup === true} />
        </div>
      )}

      {/* Hover action bar (mouse). On touch it shows under the message instead,
          see below: floating over the row it covered the name and the text. */}
      {!message.deleted && !editing && (
        <div
          className={`absolute -top-3 right-4 items-center gap-0.5 rounded-md border border-border bg-background p-0.5 shadow-sm group-hover:flex touch:!hidden ${
            tapped ? 'flex' : 'hidden'
          }`}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
